import * as React from "react";
import Button from "@mui/material/Button";
import {
  ButtonGroup as MuiButtonGroup,
  Typography as MuiTypography,
  Box
} from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import styled from "@emotion/styled";
import ArrowDropDownIcon from "../../../assets/icons/arrobutton.png";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { enUS } from "date-fns/locale";
import { styled as muiStyled } from "@mui/material/styles";

const DateRangePickerContainer = DateRangePicker as any;

const TypographyButtonSlectedValue = styled(MuiTypography)`
  color: #192f67;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: capitalize;
`;

const TypographyArrowDropDownIcon = styled(MuiTypography)`
  width: 10px;
  display: flex;
  align-items: center;
  height: 5px;
  flex-shrink: 0;
  stroke-width: 1px;
  stroke: #192f67;
`;

const ButtonGroup = styled(MuiButtonGroup)`
  display: flex;
  padding: 6px 18px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 41px;
  border: 1px solid #192f67;
`;

// Compact styling for react-date-range to remove leftover whitespace
const PickerWrapper = muiStyled("div")({
  ".rdrDefinedRangesWrapper": { display: "none" },
  ".rdrDateRangeWrapper": { gap: 0 },
  ".rdrCalendarWrapper": {
    background: "#ffffff",
    borderRadius: 8
  },
  ".rdrMonthAndYearWrapper": {
    padding: "8px 8px 0 8px"
  },
  ".rdrMonthsVertical": {
    padding: 8
  },
  ".rdrMonth": { width: "auto" },
  ".rdrDateDisplayWrapper": {
    background: "transparent",
    padding: "8px 8px 8px 8px",
    borderBottom: "1px solid #eee"
  },
  ".rdrDateDisplayItem": {
    padding: "4px 8px",
    margin: "0 4px"
  },
  ".rdrDateDisplayItemActive": {
    borderColor: "#192f67"
  }
});

const filterByName: any = {
  // Payment Metrics options
  Today: "today",
  Week: "week",
  Month: "month",
  Custom: "custom",
  // Loyalty Metrics options
  "7 days": "7days",
  "28 Days": "28days"
};

interface ButtonDropDownProps {
  children: string;
  menusOption: Array<Object> | any[];
  getTransactions: (
    data: string | any,
    value: any,
    customDateRange?: string
  ) => void | any;
  title: any;
}

const ButtonDropDown: React.FC<ButtonDropDownProps> = ({
  menusOption = [],
  getTransactions = () => {},
  title = ""
}) => {
  const [open, setOpen] = React.useState(false);
  const [customDateOpen, setCustomDateOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0); // 0 = "7 days" (first option)
  const [dateRange, setDateRange] = React.useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection"
    }
  ]);

  // Ensure dateRange is always valid
  React.useEffect(() => {
    if (
      !dateRange ||
      !dateRange[0] ||
      !dateRange[0].startDate ||
      !dateRange[0].endDate
    ) {
      setDateRange([
        {
          startDate: new Date(),
          endDate: new Date(),
          key: "selection"
        }
      ]);
    }
  }, [dateRange]);

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number
  ) => {
    const selectedOption = menusOption[index];

    if (selectedOption === "Custom") {
      setCustomDateOpen(true);
      setOpen(false);
      console.log("Opening custom date dialog"); // Debug log
      // Don't call getTransactions for Custom - just open the dialog
    } else {
      setSelectedIndex(index);
      let key = menusOption[index];
      getTransactions(filterByName[key], title);
      setOpen(false);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleCustomDateSubmit = () => {
    // Safety check for dateRange
    if (
      !dateRange ||
      !dateRange[0] ||
      !dateRange[0].startDate ||
      !dateRange[0].endDate
    ) {
      console.error("Invalid date range:", dateRange);
      return;
    }

    const startDate = dateRange[0].startDate;
    const endDate = dateRange[0].endDate;
    const dateString = `${startDate.toISOString().split("T")[0]},${
      endDate.toISOString().split("T")[0]
    }`;

    console.log("Custom date submitted:", dateString);

    // Call API with custom date range
    getTransactions("custom", title, dateString);

    // Close dialog
    setCustomDateOpen(false);

    // Update the selected index to show "Custom" as selected
    const customIndex = menusOption.findIndex((option) => option === "Custom");
    if (customIndex !== -1) {
      setSelectedIndex(customIndex);
    }
  };

  const handleCustomDateCancel = () => {
    setCustomDateOpen(false);
  };

  return (
    <React.Fragment>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        aria-label="split button"
        onClick={handleToggle}
      >
        <TypographyButtonSlectedValue>
          {menusOption[selectedIndex]}
        </TypographyButtonSlectedValue>
        <TypographyArrowDropDownIcon>
          <img src={ArrowDropDownIcon} alt="dropdown arrow" />
        </TypographyArrowDropDownIcon>
      </ButtonGroup>

      <Popper
        sx={{
          zIndex: 1
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom"
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {menusOption.map((option, index) => (
                    <MenuItem
                      key={option}
                      selected={index === selectedIndex}
                      onClick={(event) => {
                        console.log("Menu item clicked:", option, index); // Debug log
                        handleMenuItemClick(event, index);
                      }}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* Custom Date Range Popover */}
      <Popper
        sx={{
          zIndex: 9999,
          "& .MuiPaper-root": {
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            border: "1px solid #e0e0e0"
          }
        }}
        open={customDateOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-start"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom"
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleCustomDateCancel}>
                <Box sx={{ p: 0 }}>
                  <PickerWrapper>
                    <DateRangePickerContainer
                      ranges={dateRange}
                      onChange={(ranges: any) => {
                        // Accept both shapes: array or { selection }
                        const nextRanges = Array.isArray(ranges)
                          ? ranges
                          : ranges?.selection
                          ? [ranges.selection]
                          : null;
                        if (
                          nextRanges &&
                          nextRanges[0]?.startDate &&
                          nextRanges[0]?.endDate
                        ) {
                          setDateRange(nextRanges);
                        }
                      }}
                      months={1}
                      direction="vertical"
                      moveRangeOnFirstSelection={false}
                      rangeColors={["#192f67"]}
                      showSelectionPreview={true}
                      showMonthAndYearPickers={true}
                      showDateDisplay={true}
                      locale={enUS}
                      scroll={{ enabled: false }}
                      staticRanges={[]}
                      inputRanges={[]}
                    />
                  </PickerWrapper>
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        onClick={handleCustomDateCancel}
                        variant="outlined"
                        sx={{
                          borderColor: "#192f67",
                          color: "#192f67",
                          "&:hover": {
                            borderColor: "#0f1f4a",
                            backgroundColor: "#f0f2f7"
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setDateRange([
                            {
                              startDate: new Date(),
                              endDate: new Date(),
                              key: "selection"
                            }
                          ]);
                        }}
                        variant="outlined"
                        sx={{
                          borderColor: "#192f67",
                          color: "#192f67",
                          "&:hover": {
                            borderColor: "#0f1f4a",
                            backgroundColor: "#f0f2f7"
                          }
                        }}
                      >
                        Reset
                      </Button>
                    </Box>
                    <Button
                      size="small"
                      onClick={handleCustomDateSubmit}
                      variant="contained"
                      sx={{
                        backgroundColor: "#192f67",
                        "&:hover": { backgroundColor: "#0f1f4a" }
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};

export default ButtonDropDown;
